import * as i0 from '@angular/core';
import { PLATFORM_ID, Component, Inject, Input, Output, NgModule } from '@angular/core';
import DxLoadPanel from 'devextreme/ui/load_panel';
import * as i1 from 'devextreme-angular/core';
import { DxComponent, DxTemplateHost, WatcherHelper, NestedOptionHost, DxIntegrationModule, DxTemplateModule } from 'devextreme-angular/core';
import { DxoAnimationModule, DxoHideModule, DxoFromModule, DxoPositionModule, DxoAtModule, DxoBoundaryOffsetModule, DxoCollisionModule, DxoMyModule, DxoOffsetModule, DxoToModule, DxoShowModule } from 'devextreme-angular/ui/nested';
import { DxoLoadPanelAnimationModule, DxoLoadPanelAtModule, DxoLoadPanelBoundaryOffsetModule, DxoLoadPanelCollisionModule, DxoLoadPanelFromModule, DxoLoadPanelHideModule, DxoLoadPanelMyModule, DxoLoadPanelOffsetModule, DxoLoadPanelPositionModule, DxoLoadPanelShowModule, DxoLoadPanelToModule } from 'devextreme-angular/ui/load-panel/nested';
export * from 'devextreme-angular/ui/load-panel/nested';

/*!
 * devextreme-angular
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */
/* tslint:disable:max-line-length */
/**
 * The LoadPanel is an overlay UI component notifying the viewer that loading is in progress.

 */
let DxLoadPanelComponent = /*#__PURE__*/(() => {
  class DxLoadPanelComponent extends DxComponent {
    instance = null;
    /**
     * Configures UI component visibility animations. This object contains two fields: show and hide.
    
     */
    get animation() {
      return this._getOption('animation');
    }
    set animation(value) {
      this._setOption('animation', value);
    }
    /**
     * Specifies the UI component&apos;s container.
    
     */
    get container() {
      return this._getOption('container');
    }
    set container(value) {
      this._setOption('container', value);
    }
    /**
     * Specifies whether to render the UI component&apos;s content when it is displayed. If false, the content is rendered immediately.
    
     */
    get deferRendering() {
      return this._getOption('deferRendering');
    }
    set deferRendering(value) {
      this._setOption('deferRendering', value);
    }
    /**
     * The delay in milliseconds after which the load panel is displayed.
    
     */
    get delay() {
      return this._getOption('delay');
    }
    set delay(value) {
      this._setOption('delay', value);
    }
    /**
     * Specifies whether or not the UI component can be focused.
    
     */
    get focusStateEnabled() {
      return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value) {
      this._setOption('focusStateEnabled', value);
    }
    /**
     * Specifies the UI component&apos;s height.
    
     */
    get height() {
      return this._getOption('height');
    }
    set height(value) {
      this._setOption('height', value);
    }
    /**
     * Specifies whether to hide the UI component if a user clicks outside it.
    
     */
    get hideOnOutsideClick() {
      return this._getOption('hideOnOutsideClick');
    }
    set hideOnOutsideClick(value) {
      this._setOption('hideOnOutsideClick', value);
    }
    /**
     * Specifies whether to hide the widget when users scroll one of its parent elements.
    
     */
    get hideOnParentScroll() {
      return this._getOption('hideOnParentScroll');
    }
    set hideOnParentScroll(value) {
      this._setOption('hideOnParentScroll', value);
    }
    /**
     * Specifies text for a hint that appears when a user pauses on the UI component.
    
     */
    get hint() {
      return this._getOption('hint');
    }
    set hint(value) {
      this._setOption('hint', value);
    }
    /**
     * Specifies whether the UI component changes its state when a user pauses on it.
    
     */
    get hoverStateEnabled() {
      return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value) {
      this._setOption('hoverStateEnabled', value);
    }
    /**
     * A URL pointing to an image to be used as a load indicator.
    
     */
    get indicatorSrc() {
      return this._getOption('indicatorSrc');
    }
    set indicatorSrc(value) {
      this._setOption('indicatorSrc', value);
    }
    /**
     * Specifies the maximum height the UI component can reach while resizing.
    
     */
    get maxHeight() {
      return this._getOption('maxHeight');
    }
    set maxHeight(value) {
      this._setOption('maxHeight', value);
    }
    /**
     * Specifies the maximum width the UI component can reach while resizing.
    
     */
    get maxWidth() {
      return this._getOption('maxWidth');
    }
    set maxWidth(value) {
      this._setOption('maxWidth', value);
    }
    /**
     * Specifies the text displayed in the load panel. Ignored in the Material Design theme.
    
     */
    get message() {
      return this._getOption('message');
    }
    set message(value) {
      this._setOption('message', value);
    }
    /**
     * Specifies the minimum height the UI component can reach while resizing.
    
     */
    get minHeight() {
      return this._getOption('minHeight');
    }
    set minHeight(value) {
      this._setOption('minHeight', value);
    }
    /**
     * Specifies the minimum width the UI component can reach while resizing.
    
     */
    get minWidth() {
      return this._getOption('minWidth');
    }
    set minWidth(value) {
      this._setOption('minWidth', value);
    }
    /**
     * Positions the UI component.
    
     */
    get position() {
      return this._getOption('position');
    }
    set position(value) {
      this._setOption('position', value);
    }
    /**
     * Switches the UI component to a right-to-left representation.
    
     */
    get rtlEnabled() {
      return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value) {
      this._setOption('rtlEnabled', value);
    }
    /**
     * Specifies whether to shade the background when the UI component is active.
    
     */
    get shading() {
      return this._getOption('shading');
    }
    set shading(value) {
      this._setOption('shading', value);
    }
    /**
     * Specifies the shading color. Applies only if shading is enabled.
    
     */
    get shadingColor() {
      return this._getOption('shadingColor');
    }
    set shadingColor(value) {
      this._setOption('shadingColor', value);
    }
    /**
     * A Boolean value specifying whether or not to show a load indicator.
    
     */
    get showIndicator() {
      return this._getOption('showIndicator');
    }
    set showIndicator(value) {
      this._setOption('showIndicator', value);
    }
    /**
     * A Boolean value specifying whether or not to show the pane behind the load indicator.
    
     */
    get showPane() {
      return this._getOption('showPane');
    }
    set showPane(value) {
      this._setOption('showPane', value);
    }
    /**
     * A Boolean value specifying whether or not the UI component is visible.
    
     */
    get visible() {
      return this._getOption('visible');
    }
    set visible(value) {
      this._setOption('visible', value);
    }
    /**
     * Specifies the UI component&apos;s width.
    
     */
    get width() {
      return this._getOption('width');
    }
    set width(value) {
      this._setOption('width', value);
    }
    /**
     * Specifies the global attributes for the UI component&apos;s wrapper element.
    
     */
    get wrapperAttr() {
      return this._getOption('wrapperAttr');
    }
    set wrapperAttr(value) {
      this._setOption('wrapperAttr', value);
    }
    /**
    
     * A function that is executed when the UI component is rendered and each time the component is repainted.
    
    
     */
    onContentReady;
    /**
    
     * A function that is executed before the UI component is disposed of.
    
    
     */
    onDisposing;
    /**
    
     * A function that is executed after the UI component is hidden.
    
    
     */
    onHidden;
    /**
    
     * A function that is executed before the UI component is hidden.
    
    
     */
    onHiding;
    /**
    
     * A function used in JavaScript frameworks to save the UI component instance.
    
    
     */
    onInitialized;
    /**
    
     * A function that is executed after a UI component property is changed.
    
    
     */
    onOptionChanged;
    /**
    
     * A function that is executed before the UI component is displayed.
    
    
     */
    onShowing;
    /**
    
     * A function that is executed after the UI component is displayed.
    
    
     */
    onShown;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    animationChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    containerChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    deferRenderingChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    delayChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    focusStateEnabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    heightChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    hideOnOutsideClickChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    hideOnParentScrollChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    hintChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    hoverStateEnabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    indicatorSrcChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    maxHeightChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    maxWidthChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    messageChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    minHeightChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    minWidthChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    positionChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    rtlEnabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    shadingChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    shadingColorChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    showIndicatorChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    showPaneChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    visibleChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    widthChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    wrapperAttrChange;
    constructor(elementRef, ngZone, templateHost, _watcherHelper, optionHost, transferState, platformId) {
      super(elementRef, ngZone, templateHost, _watcherHelper, transferState, platformId);
      this._createEventEmitters([{
        subscribe: 'contentReady',
        emit: 'onContentReady'
      }, {
        subscribe: 'disposing',
        emit: 'onDisposing'
      }, {
        subscribe: 'hidden',
        emit: 'onHidden'
      }, {
        subscribe: 'hiding',
        emit: 'onHiding'
      }, {
        subscribe: 'initialized',
        emit: 'onInitialized'
      }, {
        subscribe: 'optionChanged',
        emit: 'onOptionChanged'
      }, {
        subscribe: 'showing',
        emit: 'onShowing'
      }, {
        subscribe: 'shown',
        emit: 'onShown'
      }, {
        emit: 'animationChange'
      }, {
        emit: 'containerChange'
      }, {
        emit: 'deferRenderingChange'
      }, {
        emit: 'delayChange'
      }, {
        emit: 'focusStateEnabledChange'
      }, {
        emit: 'heightChange'
      }, {
        emit: 'hideOnOutsideClickChange'
      }, {
        emit: 'hideOnParentScrollChange'
      }, {
        emit: 'hintChange'
      }, {
        emit: 'hoverStateEnabledChange'
      }, {
        emit: 'indicatorSrcChange'
      }, {
        emit: 'maxHeightChange'
      }, {
        emit: 'maxWidthChange'
      }, {
        emit: 'messageChange'
      }, {
        emit: 'minHeightChange'
      }, {
        emit: 'minWidthChange'
      }, {
        emit: 'positionChange'
      }, {
        emit: 'rtlEnabledChange'
      }, {
        emit: 'shadingChange'
      }, {
        emit: 'shadingColorChange'
      }, {
        emit: 'showIndicatorChange'
      }, {
        emit: 'showPaneChange'
      }, {
        emit: 'visibleChange'
      }, {
        emit: 'widthChange'
      }, {
        emit: 'wrapperAttrChange'
      }]);
      optionHost.setHost(this);
    }
    _createInstance(element, options) {
      return new DxLoadPanel(element, options);
    }
    ngOnDestroy() {
      this._destroyWidget();
    }
    /** @nocollapse */
    static ɵfac = function DxLoadPanelComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DxLoadPanelComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i1.DxTemplateHost), i0.ɵɵdirectiveInject(i1.WatcherHelper), i0.ɵɵdirectiveInject(i1.NestedOptionHost), i0.ɵɵdirectiveInject(i0.TransferState), i0.ɵɵdirectiveInject(PLATFORM_ID));
    };
    /** @nocollapse */
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: DxLoadPanelComponent,
      selectors: [["dx-load-panel"]],
      hostAttrs: ["ngSkipHydration", "true"],
      inputs: {
        animation: "animation",
        container: "container",
        deferRendering: "deferRendering",
        delay: "delay",
        focusStateEnabled: "focusStateEnabled",
        height: "height",
        hideOnOutsideClick: "hideOnOutsideClick",
        hideOnParentScroll: "hideOnParentScroll",
        hint: "hint",
        hoverStateEnabled: "hoverStateEnabled",
        indicatorSrc: "indicatorSrc",
        maxHeight: "maxHeight",
        maxWidth: "maxWidth",
        message: "message",
        minHeight: "minHeight",
        minWidth: "minWidth",
        position: "position",
        rtlEnabled: "rtlEnabled",
        shading: "shading",
        shadingColor: "shadingColor",
        showIndicator: "showIndicator",
        showPane: "showPane",
        visible: "visible",
        width: "width",
        wrapperAttr: "wrapperAttr"
      },
      outputs: {
        onContentReady: "onContentReady",
        onDisposing: "onDisposing",
        onHidden: "onHidden",
        onHiding: "onHiding",
        onInitialized: "onInitialized",
        onOptionChanged: "onOptionChanged",
        onShowing: "onShowing",
        onShown: "onShown",
        animationChange: "animationChange",
        containerChange: "containerChange",
        deferRenderingChange: "deferRenderingChange",
        delayChange: "delayChange",
        focusStateEnabledChange: "focusStateEnabledChange",
        heightChange: "heightChange",
        hideOnOutsideClickChange: "hideOnOutsideClickChange",
        hideOnParentScrollChange: "hideOnParentScrollChange",
        hintChange: "hintChange",
        hoverStateEnabledChange: "hoverStateEnabledChange",
        indicatorSrcChange: "indicatorSrcChange",
        maxHeightChange: "maxHeightChange",
        maxWidthChange: "maxWidthChange",
        messageChange: "messageChange",
        minHeightChange: "minHeightChange",
        minWidthChange: "minWidthChange",
        positionChange: "positionChange",
        rtlEnabledChange: "rtlEnabledChange",
        shadingChange: "shadingChange",
        shadingColorChange: "shadingColorChange",
        showIndicatorChange: "showIndicatorChange",
        showPaneChange: "showPaneChange",
        visibleChange: "visibleChange",
        widthChange: "widthChange",
        wrapperAttrChange: "wrapperAttrChange"
      },
      features: [i0.ɵɵProvidersFeature([DxTemplateHost, WatcherHelper, NestedOptionHost]), i0.ɵɵInheritDefinitionFeature],
      decls: 0,
      vars: 0,
      template: function DxLoadPanelComponent_Template(rf, ctx) {},
      dependencies: [DxIntegrationModule],
      encapsulation: 2
    });
  }
  return DxLoadPanelComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DxLoadPanelModule = /*#__PURE__*/(() => {
  class DxLoadPanelModule {
    /** @nocollapse */static ɵfac = function DxLoadPanelModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DxLoadPanelModule)();
    };
    /** @nocollapse */
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: DxLoadPanelModule
    });
    /** @nocollapse */
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [DxLoadPanelComponent, DxoAnimationModule, DxoHideModule, DxoFromModule, DxoPositionModule, DxoAtModule, DxoBoundaryOffsetModule, DxoCollisionModule, DxoMyModule, DxoOffsetModule, DxoToModule, DxoShowModule, DxoLoadPanelAnimationModule, DxoLoadPanelAtModule, DxoLoadPanelBoundaryOffsetModule, DxoLoadPanelCollisionModule, DxoLoadPanelFromModule, DxoLoadPanelHideModule, DxoLoadPanelMyModule, DxoLoadPanelOffsetModule, DxoLoadPanelPositionModule, DxoLoadPanelShowModule, DxoLoadPanelToModule, DxIntegrationModule, DxTemplateModule, DxoAnimationModule, DxoHideModule, DxoFromModule, DxoPositionModule, DxoAtModule, DxoBoundaryOffsetModule, DxoCollisionModule, DxoMyModule, DxoOffsetModule, DxoToModule, DxoShowModule, DxoLoadPanelAnimationModule, DxoLoadPanelAtModule, DxoLoadPanelBoundaryOffsetModule, DxoLoadPanelCollisionModule, DxoLoadPanelFromModule, DxoLoadPanelHideModule, DxoLoadPanelMyModule, DxoLoadPanelOffsetModule, DxoLoadPanelPositionModule, DxoLoadPanelShowModule, DxoLoadPanelToModule, DxTemplateModule]
    });
  }
  return DxLoadPanelModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { DxLoadPanelComponent, DxLoadPanelModule };
